import React, { useEffect, useState } from "react";
import api, { formatApiError, resolveMedia } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload } from "lucide-react";
import MediaUpload from "@/components/MediaUpload";
import RichTextEditor from "@/components/RichTextEditor";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-neutral-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Input(props) {
  return <input {...props} className={`w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))] focus:ring-1 focus:ring-[hsl(var(--brand-green-strong))] ${props.className || ""}`} />;
}
function Textarea(props) {
  return <textarea {...props} className={`w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))] focus:ring-1 focus:ring-[hsl(var(--brand-green-strong))] min-h-[100px] ${props.className || ""}`} />;
}

function ImageUpload({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  async function handle(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Bild hochgeladen");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload fehlgeschlagen");
    } finally { setBusy(false); }
  }
  return (
    <div data-testid={testid}>
      {value && <img src={resolveMedia(value)} alt="" className="w-full max-w-md aspect-[16/9] object-cover border border-brand-line mb-2" />}
      <div className="flex items-center gap-3">
        <label className="inline-flex items-center gap-2 border border-brand-line px-3 py-2 text-xs cursor-pointer hover:bg-neutral-50">
          <Upload className="h-3.5 w-3.5" /> {value ? "Ersetzen" : "Bild hochladen"}
          <input type="file" accept="image/*" className="hidden" onChange={handle} disabled={busy} />
        </label>
        {value && (
          <button type="button" onClick={() => onChange("")} className="text-xs text-neutral-500 hover:text-red-600 inline-flex items-center gap-1">
            <Trash2 className="h-3.5 w-3.5" /> Entfernen
          </button>
        )}
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="oder URL eingeben" className="flex-1" />
      </div>
    </div>
  );
}

export default function AdminHomepage() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/homepage").then((r) => setData(r.data)); }, []);

  if (!data) return <p className="text-sm text-neutral-500">Lade…</p>;

  function update(path, value) {
    setData((d) => {
      const next = JSON.parse(JSON.stringify(d));
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  }

  function addBlock() {
    setData((d) => ({ ...d, info: { ...d.info, blocks: [...(d.info.blocks || []), { heading: "", body: "", image_url: "" }] } }));
  }
  function removeBlock(i) {
    setData((d) => ({ ...d, info: { ...d.info, blocks: d.info.blocks.filter((_, idx) => idx !== i) } }));
  }

  async function save() {
    setSaving(true);
    try {
      await api.put("/homepage", data);
      toast.success("Startseite gespeichert");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-12 pb-20" data-testid="admin-homepage">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-semibold text-3xl mb-1">Startseite bearbeiten</h1>
          <p className="text-sm text-neutral-500">Hero, allgemeine Informationen und Baufortschritt pflegen.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-homepage-save" className="bg-[hsl(var(--brand-green-dark))] text-white px-6 py-2.5 text-sm hover:bg-[hsl(var(--brand-green-strong))] disabled:opacity-50">
          {saving ? "Speichern…" : "Speichern"}
        </button>
      </header>

      <section className="bg-white border border-brand-line p-8 space-y-5">
        <h2 className="font-semibold text-xl">Hero</h2>
        <Field label="Projekttitel"><Input value={data.hero.title} onChange={(e) => update("hero.title", e.target.value)} /></Field>
        <Field label="Unterzeile">
          <RichTextEditor value={data.hero.subtitle} onChange={(html) => update("hero.subtitle", html)} minHeight={100} placeholder="Unterzeile / Einleitung…" testid="hero-subtitle-editor" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Status-Label"><Input value={data.hero.status_label} onChange={(e) => update("hero.status_label", e.target.value)} /></Field>
          <Field label="Status-Wert"><Input value={data.hero.status_value} onChange={(e) => update("hero.status_value", e.target.value)} /></Field>
        </div>
        <Field label="Hero-Bild"><ImageUpload value={data.hero.image_url} onChange={(v) => update("hero.image_url", v)} testid="hero-image" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Button 1 Text"><Input value={data.hero.cta_primary_label} onChange={(e) => update("hero.cta_primary_label", e.target.value)} /></Field>
          <Field label="Button 1 Link"><Input value={data.hero.cta_primary_link} onChange={(e) => update("hero.cta_primary_link", e.target.value)} /></Field>
          <Field label="Button 2 Text"><Input value={data.hero.cta_secondary_label} onChange={(e) => update("hero.cta_secondary_label", e.target.value)} /></Field>
          <Field label="Button 2 Link"><Input value={data.hero.cta_secondary_link} onChange={(e) => update("hero.cta_secondary_link", e.target.value)} /></Field>
        </div>
      </section>

      <section className="bg-white border border-brand-line p-8 space-y-5">
        <h2 className="font-semibold text-xl">Allgemeine Informationen</h2>
        <Field label="Überschrift"><Input value={data.info.heading} onChange={(e) => update("info.heading", e.target.value)} /></Field>
        <Field label="Einleitung">
          <RichTextEditor value={data.info.intro} onChange={(html) => update("info.intro", html)} minHeight={120} placeholder="Einleitung…" testid="info-intro-editor" />
        </Field>

        <div className="space-y-6">
          {(data.info.blocks || []).map((b, i) => (
            <div key={i} className="border border-brand-line p-5 space-y-4" data-testid={`info-block-edit-${i}`}>
              <div className="flex justify-between items-center">
                <p className="text-xs uppercase tracking-wider text-neutral-500">Block {i + 1}</p>
                <button type="button" onClick={() => removeBlock(i)} className="text-xs text-neutral-500 hover:text-red-600 inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Entfernen</button>
              </div>
              <Field label="Überschrift"><Input value={b.heading} onChange={(e) => { const blocks = [...data.info.blocks]; blocks[i] = { ...b, heading: e.target.value }; update("info.blocks", blocks); }} /></Field>
              <Field label="Text">
                <RichTextEditor
                  value={b.body}
                  onChange={(html) => { const blocks = [...data.info.blocks]; blocks[i] = { ...b, body: html }; update("info.blocks", blocks); }}
                  minHeight={140}
                  placeholder="Text…"
                  testid={`info-block-body-${i}`}
                />
              </Field>
              <Field label="Bild (optional)"><ImageUpload value={b.image_url} onChange={(v) => { const blocks = [...data.info.blocks]; blocks[i] = { ...b, image_url: v }; update("info.blocks", blocks); }} /></Field>
              <Field label="Weitere Medien (Bilder, Videos, PDFs)">
                <MediaUpload
                  value={b.media || []}
                  onChange={(media) => { const blocks = [...data.info.blocks]; blocks[i] = { ...b, media }; update("info.blocks", blocks); }}
                  testid={`info-block-media-${i}`}
                />
              </Field>
            </div>
          ))}
          <button type="button" onClick={addBlock} data-testid="add-info-block" className="inline-flex items-center gap-2 border border-brand-line px-3 py-2 text-xs hover:bg-neutral-50">
            <Plus className="h-3.5 w-3.5" /> Block hinzufügen
          </button>
        </div>

        <div className="border-t border-brand-line pt-5 mt-2">
          <Field label="Bildergalerie auf der Informationen-Seite" hint="Bilder, die als visuelle Auflockerung oberhalb der Inhalte gezeigt werden. Reihenfolge per Drag & Drop sortierbar.">
            <MediaUpload
              value={data.info.gallery || []}
              onChange={(gallery) => update("info.gallery", gallery)}
              testid="info-gallery"
            />
          </Field>
        </div>
      </section>

      <section className="bg-white border border-brand-line p-8 space-y-5">
        <h2 className="font-semibold text-xl">Baufortschritt</h2>
        <Field label="Überschrift"><Input value={data.progress.heading} onChange={(e) => update("progress.heading", e.target.value)} /></Field>
        <Field label="Status (kurz)"><Input value={data.progress.status} onChange={(e) => update("progress.status", e.target.value)} /></Field>
        <Field label="Text">
          <RichTextEditor value={data.progress.body} onChange={(html) => update("progress.body", html)} minHeight={140} placeholder="Beschreibung des Baufortschritts…" testid="progress-body-editor" />
        </Field>
        <Field label="Bild"><ImageUpload value={data.progress.image_url} onChange={(v) => update("progress.image_url", v)} /></Field>
        <Field label="Datum der letzten Aktualisierung"><Input type="date" value={(data.progress.last_updated || "").slice(0, 10)} onChange={(e) => update("progress.last_updated", e.target.value ? new Date(e.target.value).toISOString() : "")} /></Field>
        <Field label="Video-URL (optional)"><Input value={data.progress.video_url || ""} onChange={(e) => update("progress.video_url", e.target.value)} /></Field>
      </section>
    </div>
  );
}
